/**
 * Favorites, shared between the star button wherever it appears (the registry
 * table, the company profile pane) and the Favorites page.
 *
 * Same shape as ShortlistProvider (see shortlist-context.jsx's header
 * comment) and for the same reason: several surfaces need to know "is this
 * company starred?" and toggle it, and without one shared store they drift
 * the moment a star is clicked on one screen and read on another.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { favoritesApi } from './api.js';
import { useAuth } from './auth-context.jsx';

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Company numbers with a request in flight, so a star can disable itself
  // without freezing the rest of the list.
  const [pending, setPending] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await favoritesApi.list();
      setItems(data.items ?? []);
      setError(null);
    } catch (caught) {
      setError(caught.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markPending = useCallback((companyNumbers, active) => {
    setPending((current) => {
      const next = new Set(current);
      for (const number of companyNumbers) {
        if (active) next.add(number);
        else next.delete(number);
      }
      return next;
    });
  }, []);

  const add = useCallback(
    async (companyNumber) => {
      markPending([companyNumber], true);
      try {
        await favoritesApi.add(companyNumber);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending([companyNumber], false);
      }
    },
    [markPending, refresh],
  );

  const remove = useCallback(
    async (companyNumber) => {
      markPending([companyNumber], true);
      try {
        await favoritesApi.remove(companyNumber);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending([companyNumber], false);
      }
    },
    [markPending, refresh],
  );

  const toggle = useCallback(
    async (companyNumber, isFavorite) => {
      if (isFavorite) await remove(companyNumber);
      else await add(companyNumber);
    },
    [add, remove],
  );

  const companyNumbers = useMemo(() => new Set(items.map((item) => item.companyNumber)), [items]);

  const value = useMemo(
    () => ({
      items,
      companyNumbers,
      count: items.length,
      isLoading,
      error,
      has: (companyNumber) => companyNumbers.has(companyNumber),
      isPending: (companyNumber) => pending.has(companyNumber),
      add,
      remove,
      toggle,
      refresh,
    }),
    [items, companyNumbers, isLoading, error, pending, add, remove, toggle, refresh],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error('useFavorites must be used inside a FavoritesProvider.');
  return context;
}
